import axios from "axios";
import { environment } from "environment/environment";
import { SectorStrategyApiUrl } from "./SectorStrategyApiUrl";

const strategyApi = axios.create({
  baseURL: `${environment.stockApiBaseUrl}${SectorStrategyApiUrl.basePath}`,
  headers: { "Content-Type": "application/json" },
});

const executionApi = axios.create({
  baseURL: `${environment.stockApiBaseUrl}${SectorStrategyApiUrl.executionBasePath}`,
  headers: { "Content-Type": "application/json" },
});

const unwrap = (response) => {
  const body = response?.data;
  if (body?.status === false) {
    throw new Error(body.message || "Sector strategy operation failed");
  }
  return body?.data ?? null;
};

const requestConfig = (accessToken, params) => ({
  ...(params ? { params } : {}),
  ...(accessToken
    ? { headers: { Authorization: `Bearer ${accessToken}` } }
    : {}),
});

export const getSectorStrategies = async (userId, accessToken) =>
  unwrap(await strategyApi.get(
    SectorStrategyApiUrl.getByUser,
    requestConfig(accessToken, { userId })
  )) || [];

export const getSectorStrategyById = async (id, userId, accessToken) =>
  unwrap(await strategyApi.get(
    SectorStrategyApiUrl.getById,
    requestConfig(accessToken, { id, userId })
  ));

export const saveSectorStrategy = async (payload, accessToken) =>
  unwrap(await strategyApi.post(
    SectorStrategyApiUrl.save,
    payload,
    requestConfig(accessToken)
  ));

export const updateSectorStrategy = async (id, userId, payload, accessToken) =>
  unwrap(await strategyApi.put(SectorStrategyApiUrl.update, payload, {
    ...requestConfig(accessToken, { id, userId }),
  }));

export const getSectorStrategyExecutionCapabilities = async (accessToken) =>
  unwrap(await executionApi.get(
    SectorStrategyApiUrl.capabilities,
    requestConfig(accessToken)
  ));

export const getSectorStrategyTrades = async (strategyId, userId, accessToken) =>
  unwrap(await executionApi.get(SectorStrategyApiUrl.trades, {
    ...requestConfig(accessToken, { strategyId, userId }),
  })) || [];

export const getSectorStrategyEntryCalculations = async (
  strategyId,
  userId,
  accessToken
) => unwrap(await executionApi.get(SectorStrategyApiUrl.entryCalculations, {
  ...requestConfig(accessToken, { strategyId, userId }),
})) || [];

export const getSectorStrategyCandidates = async (strategyId, userId, accessToken) =>
  unwrap(await executionApi.get(SectorStrategyApiUrl.candidates, {
    ...requestConfig(accessToken, { strategyId, userId }),
  })) || {};

export const getSectorStrategyExecutionHistory = async (
  userId,
  tradeDate,
  accessToken
) => unwrap(await executionApi.get(SectorStrategyApiUrl.history, {
  ...requestConfig(accessToken, { userId, tradeDate }),
})) || [];

export const getSectorStrategyExecutionHistoryForRange = async (
  userId,
  fromTradeDate,
  toTradeDate,
  accessToken
) => unwrap(await executionApi.get(`${SectorStrategyApiUrl.history}/range`, {
  ...requestConfig(accessToken, {
    userId,
    fromTradeDate,
    toTradeDate,
  }),
})) || [];

export const getSectorStrategyPerformance = async (userId, accessToken) =>
  unwrap(await executionApi.get(SectorStrategyApiUrl.performance, {
    ...requestConfig(accessToken, { userId }),
  }));

export const squareOffSectorStrategyTrade = async (tradeId, userId, accessToken) =>
  unwrap(await executionApi.post(SectorStrategyApiUrl.squareOff, null, {
    ...requestConfig(accessToken, { tradeId, userId }),
  }));

export const squareOffSectorStrategy = async (strategyId, userId, accessToken) =>
  unwrap(await executionApi.post(SectorStrategyApiUrl.squareOffStrategy, null, {
    ...requestConfig(accessToken, { strategyId, userId }),
  })) || [];
